<template>
  <div>
    <v-container       
      class="full-height"
      fluid
    >
      <v-card>
        <v-card-title>Family Details Report</v-card-title>
        <v-row class="px-4">
          <v-col
            cols="8"
            class="pt-0"
          >
            <report-selected-employees
              :value="selectedEmployees"
              :pending-removal="selectionRemoval.employees"
              @input="setSelectedEmployees"
              @confirm-removal="confirmSelectionRemoval"
              @cancel-removal="cancelSelectionRemoval"
            />
            <v-data-table 
              v-model="selectedEmployees"
              v-validate="'required'"
              dense
              :error-messages="errors.collect('selectedEmployees')"
              data-vv-name="selectedEmployees"
              data-vv-as="employees"
              :loading="tableLoading"
              :headers="headers"
              :items="filteredEmployees"
              hide-default-footer
              fixed-header
              disable-pagination
              show-select
              item-key="employee_uid"
              item-value="employee_uid"
              class="elevation-2 mb-3"
              height="420px"
            >
              <template v-slot:top>
                <h5 class="font-weight-bold py-2 mb-0 pl-2">
                  Employee Selection:
                </h5>
              </template> 
              <template v-slot:item.ic_number="{ item }">
                {{ ((item.person || {}).identity_card || {}).ic_number ? ((item.person || {}).identity_card || {}).ic_number : ((item.person || {}).identity_card || {}).passport_number }}
              </template> 
            </v-data-table>
            <span class="red--text text--lighten-1 caption">{{ errors.first('selectedEmployees') }}</span>
          </v-col>

          <v-col
            cols="4"
            class="pt-0"
          >
            <v-text-field
              v-model="searchInput"
              placeholder="Filter employee by name"
              append-icon="search"
              label="Search Employee"
              hide-no-data
              hide-selected
            />

            <v-text-field
              v-model="searchIC"
              placeholder="Filter employee by IC"
              append-icon="search"
              label="Search Employee IC"
              hide-no-data
              hide-selected
            />            

            <v-autocomplete
              v-model="filters.companies"
              :items="companies"
              item-text="name"
              item-value="id"
              label="Company"
              placeholder="Filter employee by company"
              clearable
              hide-selected
              multiple
              small-chips
              deletable-chips
              chips
            />

            <v-autocomplete
              v-model="filters.positions"
              :items="positions"
              item-text="label"
              item-value="id"
              value="id"
              label="Position"
              placeholder="Filter employee by position"
              clearable
              hide-selected
              multiple
              small-chips
              deletable-chips
              chips
            />

            <v-autocomplete
              v-model="filters.departments"
              :items="departments"
              item-text="name"
              item-value="id"
              value="id"
              label="Department"
              placeholder="Filter employee by department"
              clearable
              hide-selected
              multiple
              deletable-chips
              small-chips
              chips
            />

            <v-autocomplete
              v-model="filters.costCenters"
              :items="filteredCostCentersByCompany"
              item-text="label"
              item-value="id"
              label="Cost Center"
              placeholder="Filter employee by cost center"
              clearable
              hide-selected
              multiple
              small-chips
              deletable-chips
              chips
            />

            <v-card-actions>
              <v-spacer />
              <v-btn
                block
                :loading="progressing"
                color="hrissecondary"     
                class="white--text"     
                @click="generate"
              >
                Generate
              </v-btn>
            </v-card-actions>
          </v-col>
        </v-row>  
      </v-card>
    </v-container>  
  </div>      
</template>
<script>
  import ReportSelectedEmployees from '@/components/hris/reports/shared/SelectedEmployees'
  import reportEmployeeSelection from '@/components/hris/reports/shared/reportEmployeeSelection'

  export default {
    components: {
      ReportSelectedEmployees,
    },
    mixins: [reportEmployeeSelection],
    $_veeValidate: {
      validator: 'new', 
    },
    props: {
      employees: {
        type: Array,
        required: true,
      },
      positions: {
        type: Array,
        required: true,
      },
      departments: {
        type: Array,
        required: true,
      },
      companies: {
        type: Array,
        required: true,
      },
      costCenters: {
        type: Array,
        default: () => [],
      },
    },
    data () {
      return {
        progressing: false,
        tableLoading: false,
        searchInput: '',
        searchIC: '',
        activeEmployees: [],
        filters: {
          companies: '',
          positions: '',
          departments: '',
          costCenters: '',
        },
        selectedCompanies: [],
        selectedEmployees: [],
        headers: [
          {
            text: 'Name',
            align: 'left',
            sortable: true,
            value: 'person.name',
          },
          {
            text: 'IC',
            align: 'left',
            sortable: true,
            value: 'ic_number',
          },
          {
            text: 'Department',
            align: 'left',
            sortable: true,
            value: 'department.name',
          },
          {
            text: 'Position',
            align: 'left',
            sortable: true,
            value: 'current_position.position.name',
          }, 
          {
            text: 'Resigned Date',
            align: 'left',
            sortable: true,
            value: 'end_date',
          },       
        ],
      }
    },
    computed: {
      filteredCostCentersByCompany () {
        const selectedCompanies = this.filters.companies || []

        if (!selectedCompanies.length) return this.costCenters

        return this.costCenters.filter(cc => selectedCompanies.includes(cc.selected_company_uid))
      },
      baseEmployees () {
        let filteredCompanies = this.customFilterCompanies(this.activeEmployees)
        let filteredPositions = this.customFilterPositions(filteredCompanies)
        let filteredDepartments = this.customFilterDepartments(filteredPositions)
        let filteredCostCenters = this.customFilterCostCenters(filteredDepartments)
        return filteredCostCenters
      },
    },
    created () {
      this.tableLoading = true

      this.activeEmployees = this.employees.filter(employee => {
        return (employee.end_date === null || employee.end_date > this.$moment().format('YYYY-MM-DD'))
      })

      this.tableLoading = false
    },
    methods: {
      prepareFormData () {
        return {
          employeeUids: this.getEmployeeUids(),
          // resign_date: this.resignDate,
        }
      },
      generate () {
        this.$validator.validateAll()
          .then(result => {
            if (!result) return

            this.progressing = true

            this.$api.employeeReports().familyDetailsReport(this.prepareFormData())
              .then(res => {
                let blob = new Blob([res.data], { type: res.data.type })
                let link = document.createElement('a')
                link.href = window.URL.createObjectURL(blob)
                link.setAttribute('download', 'Family Details Report.xlsx')
                link.click()

                this.progressing = false
                this.$snack({ message: 'Report downloaded successfully' })
              })
              .catch(() => {
                this.progressing = false
                this.$snack({
                  color: 'error',
                  message: 'Failed to generate report. Please contact IT support',
                })
              })
          })
      },

      getEmployeeUids () {
        let employeeUids = []

        this.selectedEmployees.map(item => {
          employeeUids.push(item.employee_uid)
        })

        return employeeUids
      },

      customFilterCompanies (employeesList) {
        if (this.filters.companies.length === 0) return employeesList

        return employeesList.filter(employee => {
          if (employee.company_employees.length <= 0) return 

          let companyEmployees = employee.company_employees.filter(companyEmployee => {
            return companyEmployee.is_active_pay_status === 1 && 
              companyEmployee.start_date <= this.$moment().format('YYYY-MM-DD') && 
              (companyEmployee.end_date === null || companyEmployee.end_date >= this.$moment().format('YYYY-MM-DD'))
          })

          if (companyEmployees.length > 0) {
            let latestPayrollCompany = companyEmployees[0].company_uid
  
            return _.intersection([latestPayrollCompany], this.filters.companies).length > 0
          }
        })
      },

      customFilterPositions (employeesList) {
        if (this.filters.positions.length === 0) return employeesList

        return employeesList.filter(employee => {
          let positionId = (employee.current_position || {}).position_id
  
          return _.intersection([positionId], this.filters.positions).length > 0
        })
      },

      customFilterDepartments (employeesList) {
        if (this.filters.departments.length === 0) return employeesList

        return employeesList.filter(employee => {
          return _.intersection([employee.department_id], this.filters.departments).length > 0
        })
      },

      customFilterCostCenters (employeesList) {
        if (this.filters.costCenters.length === 0) return employeesList

        const today = this.$moment().format('YYYY-MM-DD')
        const from = today
        const to = today

        return employeesList.filter(employee => {
          const costCenterIds = _.flatMap(employee.company_employees || [], companyEmployee => companyEmployee.company_employee_cost_centers || [])
            .filter(costCenter => (!costCenter.start_date || costCenter.start_date <= to) && (!costCenter.end_date || costCenter.end_date >= from))
            .map(costCenter => costCenter.cost_center_id)

          return _.intersection(costCenterIds, this.filters.costCenters).length > 0
        })
      },
    },
  }
</script>
